import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiFetch, setToken } from '../lib/api';
import { useAuth } from '../hooks/useAuth';

export default function AcceptInvitePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loginWithGoogle } = useAuth();
  const token = searchParams.get('token');
  const artistName = searchParams.get('artist') || 'this team';
  const isExisting = searchParams.get('existing') === '1';

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Already logged in — they're already linked (server did it at invite time)
  // Show success and redirect
  useEffect(() => {
    if (user) {
      setSuccess(true);
      const dest = (user.role === 'admin') ? '/admin' : '/viewer';
      const timer = setTimeout(() => navigate(dest, { replace: true }), 2000);
      return () => clearTimeout(timer);
    }
  }, [user, navigate]);

  // No token and not existing user flow — nothing to do
  useEffect(() => {
    if (!token && !isExisting && !user) navigate('/login', { replace: true });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    setLoading(true); setError('');
    try {
      const data = await apiFetch('/auth/invite-accept', {
        method: 'POST',
        body: JSON.stringify({ token, firstName, lastName, password }),
      });
      setToken(data.token);
      localStorage.setItem('ds_user', JSON.stringify(data.user));
      window.dispatchEvent(new Event('daysheets:login'));
      setSuccess(true);
      const dest = (data.user.role === 'admin') ? '/admin' : '/viewer';
      setTimeout(() => navigate(dest, { replace: true }), 1500);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const inputStyle = {
    width: '100%', background: 'var(--card)', border: '1px solid var(--border)',
    borderRadius: 12, padding: '14px 16px', fontSize: 15, color: 'var(--text)',
    outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
  };

  // ── Success state ──
  if (success) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{ textAlign: 'center', maxWidth: 400 }}>
          <div style={{ width: 80, height: 80, borderRadius: '50%', background: 'rgba(34,197,94,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
            <i className="fa-solid fa-check" style={{ fontSize: 36, color: '#22c55e' }}></i>
          </div>
          <h1 style={{ fontSize: 28, fontWeight: 900, color: 'var(--text)', marginBottom: 8 }}>You're in!</h1>
          <p style={{ fontSize: 16, color: 'var(--text2)', lineHeight: 1.5 }}>
            You've been added to <strong style={{ color: 'var(--text)' }}>{artistName}</strong>
          </p>
          <p style={{ fontSize: 13, color: 'var(--text3)', marginTop: 16 }}>Redirecting...</p>
        </div>
      </div>
    );
  }

  // ── Existing user, not logged in — prompt to sign in ──
  if (isExisting && !user) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: '-20%', left: '50%', transform: 'translateX(-50%)', width: 600, height: 600, borderRadius: '50%', background: 'radial-gradient(ellipse, rgba(30,64,175,0.08), transparent 70%)', pointerEvents: 'none' }} />
        <div style={{ width: '100%', maxWidth: 400, position: 'relative', zIndex: 1 }}>
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
              <img src="/icon.png" alt="Day Sheets" style={{ width: 40, height: 40, borderRadius: 12, boxShadow: '0 4px 20px rgba(30,64,175,0.3)' }} />
              <span style={{ fontSize: 24, fontWeight: 900, color: 'var(--text)', letterSpacing: -0.5 }}>Day Sheets</span>
            </div>
            <h1 style={{ fontSize: 28, fontWeight: 900, color: 'var(--text)', marginBottom: 6, letterSpacing: -1 }}>Welcome back!</h1>
            <p style={{ fontSize: 14, color: 'var(--text2)', lineHeight: 1.5 }}>
              Sign in to join <strong style={{ color: 'var(--text)' }}>{artistName}</strong>
            </p>
          </div>

          <div style={{ background: 'var(--card)', borderRadius: 20, border: '1px solid var(--border)', padding: 28, boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
            <button onClick={loginWithGoogle}
              style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                background: '#fff', border: 'none', borderRadius: 12, padding: '14px',
                cursor: 'pointer', fontFamily: 'inherit', fontSize: 15, fontWeight: 700, color: '#333', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', marginBottom: 16 }}>
              <svg width="18" height="18" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Sign in with Google
            </button>

            <button onClick={() => navigate('/login')}
              style={{ width: '100%', padding: '14px', borderRadius: 12, background: 'linear-gradient(135deg,#1E40AF,#1E40AF)', border: 'none', color: '#fff', fontSize: 15, fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 4px 12px rgba(30,64,175,0.3)' }}>
              Sign in with Email
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── New user — create account ──
  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: '-20%', left: '50%', transform: 'translateX(-50%)', width: 600, height: 600, borderRadius: '50%', background: 'radial-gradient(ellipse, rgba(30,64,175,0.08), transparent 70%)', pointerEvents: 'none' }} />
      <div style={{ width: '100%', maxWidth: 400, position: 'relative', zIndex: 1 }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
            <img src="/icon.png" alt="Day Sheets" style={{ width: 40, height: 40, borderRadius: 12, boxShadow: '0 4px 20px rgba(30,64,175,0.3)' }} />
            <span style={{ fontSize: 24, fontWeight: 900, color: 'var(--text)', letterSpacing: -0.5 }}>Day Sheets</span>
          </div>
          <h1 style={{ fontSize: 28, fontWeight: 900, color: 'var(--text)', marginBottom: 6, letterSpacing: -1 }}>You're invited!</h1>
          <p style={{ fontSize: 14, color: 'var(--text2)', lineHeight: 1.5 }}>
            Create your account to join <strong style={{ color: 'var(--text)' }}>{artistName}</strong>
          </p>
        </div>

        <div style={{ background: 'var(--card)', borderRadius: 20, border: '1px solid var(--border)', padding: 28, boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
          <button onClick={loginWithGoogle}
            style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
              background: '#fff', border: 'none', borderRadius: 12, padding: '14px',
              cursor: 'pointer', fontFamily: 'inherit', fontSize: 15, fontWeight: 700, color: '#333', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', marginBottom: 0 }}>
            <svg width="18" height="18" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            Continue with Google
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '20px 0' }}>
            <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
            <span style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 600, letterSpacing: 1 }}>OR</span>
            <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
          </div>

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <input value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="First name"
                required autoFocus style={inputStyle} />
              <input value={lastName} onChange={e => setLastName(e.target.value)} placeholder="Last name"
                style={inputStyle} />
            </div>
            <input value={password} onChange={e => setPassword(e.target.value)} placeholder="Create a password (min 6 chars)"
              type="password" required style={inputStyle} />
            {error && (
              <div style={{ padding: '10px 14px', background: 'rgba(248,81,73,0.12)', border: '1px solid rgba(248,81,73,0.3)', borderRadius: 10, fontSize: 13, color: 'var(--red)' }}>
                {error}
              </div>
            )}
            <button type="submit" disabled={loading}
              style={{ background: 'linear-gradient(135deg,#1E40AF,#1E40AF)', color: 'white', border: 'none', borderRadius: 12, padding: '14px', fontSize: 15, fontWeight: 800, cursor: loading ? 'wait' : 'pointer', fontFamily: 'inherit', marginTop: 4, boxShadow: '0 4px 12px rgba(30,64,175,0.3)' }}>
              {loading ? 'Creating account...' : 'Create Account & Sign In'}
            </button>
          </form>
        </div>

        <p style={{ textAlign: 'center', fontSize: 13, color: 'var(--text2)', marginTop: 20 }}>
          Already have an account?{' '}
          <button onClick={() => navigate('/login')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--blue)', fontWeight: 700, fontSize: 13, fontFamily: 'inherit' }}>
            Sign in
          </button>
        </p>
      </div>
    </div>
  );
}
